import { Chip, Stack, Tooltip } from '@mui/material';

export const MODEL_LABELS = {
  current_smart: 'Продвинутый прогноз',
  autogluon: 'AutoGluon',
  nixtla: 'Nixtla',
  mixed: 'Смешанный прогноз',
};

export const METRIC_LABELS = {
  WAPE: 'WAPE',
  wape: 'WAPE',
  MAPE: 'MAPE',
  mape: 'MAPE',
  Bias: 'Смещение',
  bias: 'Смещение',
  Calibration: 'Поправка',
  calibration: 'Поправка',
};

export const REASON_LABELS = {
  best_wape_4_windows: 'лучше других моделей по точности за последние 4 проверки',
  lower_wape: 'ниже ошибка прогноза',
  stable_bias: 'смещение в допустимых пределах',
  enough_fact_qty: 'достаточно фактических продаж для надёжной оценки',
  enough_windows: 'достаточно проверочных окон',
  calibration_improves_wape: 'поправка улучшает точность прогноза',
  active_no_degradation: 'нет ухудшения на регулярных товарах',
  top_volume_no_degradation: 'нет ухудшения на товарах с большим объёмом',
  manual_approval_required: 'требуется ручное подтверждение',
};

export const STATUS_LABELS = {
  pending: 'Ожидает решения',
  approved: 'Принято',
  rejected: 'Отклонено',
  snoozed: 'Отложено',
  active: 'Активно',
  inactive: 'Неактивно',
  draft: 'Черновик',
};

export const COHORT_LABELS = {
  inactive: 'Без продаж',
  sparse: 'Редкие продажи',
  active: 'Регулярные продажи',
  top_volume: 'Высокий объём',
  overall: 'Все товары',
};

export const MP_LABELS = {
  1: 'WB',
  2: 'Ozon',
  3: 'Yandex',
  wb: 'WB',
  ozon: 'Ozon',
  yandex: 'Yandex',
};

export function modelLabel(value) {
  if (value == null || value === '') return '—';
  return MODEL_LABELS[value] || String(value);
}

export function metricLabel(value) {
  if (value == null || value === '') return '—';
  return METRIC_LABELS[value] || String(value);
}

export function reasonLabel(value) {
  if (value == null || value === '') return 'Причина не указана';
  return REASON_LABELS[value] || 'дополнительный сигнал качества прогноза';
}

export function cohortLabel(value) {
  if (value == null || value === '') return 'Все товары';
  return COHORT_LABELS[value] || String(value);
}

export function marketplaceLabel(value) {
  if (value == null || value === '') return 'Все МП';
  return MP_LABELS[value] || String(value);
}

export function fmtNumber(value, digits = 1) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: digits });
}

export function fmtPercent(value, digits = 1) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const pct = Math.abs(n) <= 1 ? n * 100 : n;
  return `${pct.toLocaleString('ru-RU', { maximumFractionDigits: digits })}%`;
}

export function fmtFactor(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `×${n.toLocaleString('ru-RU', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

export function extractForecastMetadata(...sources) {
  for (const source of sources) {
    const obj = parseMaybeJson(source);
    if (!obj) continue;

    const nested = [
      obj.forecast_metadata,
      obj.forecastMetadata,
      obj.forecast_meta,
      obj.forecastMeta,
      obj.routing,
      obj.model_routing,
      obj.calibration,
    ].map(parseMaybeJson).find(Boolean);

    const meta = nested || obj;
    const model = meta.selected_model || meta.model_name || meta.model || meta.routing_model || meta.route_model;
    const calibrationFactor = meta.calibration_factor ?? meta.factor ?? meta.calibrationFactor ?? meta.calibration?.factor;
    const routingVersion = meta.routing_version || meta.routingVersion || meta.route_version || meta.model_version || meta.version;
    const horizonBucket = meta.horizon_bucket || meta.horizonBucket;
    const fallbackUsed = meta.fallback_used ?? meta.fallbackUsed;
    const message = meta.message || meta.note || meta.description;

    if (model || calibrationFactor != null || routingVersion || horizonBucket || fallbackUsed != null || message) {
      return {
        model,
        calibrationFactor,
        routingVersion,
        horizonBucket,
        fallbackUsed,
        message,
      };
    }
  }
  return null;
}

export function ForecastMetadataChips({ metadata, size = 'small' }) {
  if (!metadata) return null;

  const chips = [];
  if (metadata.model) {
    chips.push({
      key: 'model',
      label: `Модель: ${modelLabel(metadata.model)}`,
      tooltip: 'Модель, выбранная правилами прогноза для этого расчёта.',
      color: 'primary',
    });
  }
  if (metadata.calibrationFactor != null) {
    chips.push({
      key: 'calibration',
      label: `Поправка: ${fmtFactor(metadata.calibrationFactor)}`,
      tooltip: 'Калибровочная поправка к прогнозу, рассчитанная по истории качества.',
      color: 'info',
    });
  }
  if (metadata.horizonBucket) {
    chips.push({
      key: 'horizon',
      label: `Горизонт: ${metadata.horizonBucket}`,
      tooltip: 'Группа дней прогноза, для которой применено правило.',
      color: 'default',
    });
  }
  if (metadata.routingVersion) {
    chips.push({
      key: 'version',
      label: `Правило: ${metadata.routingVersion}`,
      tooltip: 'Версия правила выбора модели.',
      color: 'default',
    });
  }
  if (metadata.fallbackUsed) {
    chips.push({
      key: 'fallback',
      label: 'Откат: продвинутый',
      tooltip: metadata.message || 'Для части прогноза не хватило данных Forecast Lab, использован продвинутый прогноз.',
      color: 'warning',
    });
  }
  if (!metadata.fallbackUsed && metadata.message) {
    chips.push({
      key: 'message',
      label: 'Forecast Lab',
      tooltip: metadata.message,
      color: 'success',
    });
  }

  if (!chips.length) return null;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {chips.map((chip) => (
        <Tooltip key={chip.key} title={chip.tooltip}>
          <Chip size={size} color={chip.color} variant="outlined" label={chip.label} />
        </Tooltip>
      ))}
    </Stack>
  );
}
